
import { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { BookingSEO } from '@/components/SEO/BookingSEO';
import ProviderStep from '@/components/booking/ProviderStep';
import ServiceStep from '@/components/booking/ServiceStep';
import StaffStep from '@/components/booking/StaffStep';
import TimeStep from '@/components/booking/TimeStep';
import AddonsStep from '@/components/booking/AddonsStep';
import ConfirmStep from '@/components/booking/ConfirmStep';
import BookingComplete from '@/components/booking/BookingComplete';
import { useBooking } from '@/contexts/BookingContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, ArrowLeft, ArrowRight } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';

// Function to extract query parameters
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const BookingFlow = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const mobileBottomRef = useRef<HTMLDivElement>(null);
  
  const {
    currentStep,
    setCurrentStep,
    selectedProvider,
    selectedService,
    selectedDate,
    selectedTime,
    selectedStaff,
    isBookingComplete,
    resetBooking,
    isBookingStarted,
    startBooking,
    goToNextStep,
    goToPreviousStep,
    canMoveToNextStep,
    setSelectedProvider
  } = useBooking();

  // Debug-Logging
  console.log("BookingFlow: Current step:", currentStep);
  console.log("BookingFlow: Selected provider:", selectedProvider?.name || "none");
  console.log("BookingFlow: isBookingStarted:", isBookingStarted);
  console.log("BookingFlow: isBookingComplete:", isBookingComplete);
  console.log("BookingFlow: Current path:", location.pathname);

  // Handle case where studio is selected from homepage
  useEffect(() => {
    const state = location.state;
    if (state?.selectedStudio) {
      console.log("Studio selected from homepage:", state.selectedStudio);
      startBooking();
      setSelectedProvider(state.selectedStudio);
      if (state.startStep && typeof state.startStep === 'number') {
        setCurrentStep(state.startStep);
      } else if (state.selectedStudio) {
        // If studio is selected but no step is specified, go to step 2
        console.log("Setting step to 2 because studio is selected");
        setCurrentStep(2);
      }
    }
  }, [location.state, setSelectedProvider, setCurrentStep, startBooking]);

  // Start the booking process when component mounts
  useEffect(() => {
    console.log("BookingFlow mounted, isBookingStarted:", isBookingStarted);
    if (!isBookingStarted) {
      startBooking();
      toast({
        title: "Buchungsvorgang gestartet",
        description: "Folge den Schritten, um deinen Termin zu buchen.",
        duration: 3000,
      });
    }
  }, [isBookingStarted, startBooking]);

  // Check if we're on the completion page
  const isCompletePage = location.pathname === '/booking/complete';
  
  console.log("isCompletePage:", isCompletePage);

  // Verbesserte Logik für das Zurücksetzen der Buchung
  useEffect(() => {
    // Nur Zurücksetzen, wenn wir die Buchungsseite verlassen und nicht auf die Bestätigungsseite gehen
    return () => {
      const isLeavingBookingFlow = !window.location.pathname.startsWith('/booking');
      
      if (isLeavingBookingFlow && !isBookingComplete) {
        console.log("Leaving booking flow without completing, resetting booking data");
        resetBooking();
      } else {
        console.log("Not resetting on navigation - path:", window.location.pathname, "isComplete:", isBookingComplete);
      }
    };
  }, [isBookingComplete, resetBooking]);

  // Scroll to the bottom of the mobile view when buttons appear
  useEffect(() => {
    if (isMobile && mobileBottomRef.current) {
      const timer = setTimeout(() => {
        mobileBottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 1000); // Give time for content to render
      return () => clearTimeout(timer);
    }
  }, [currentStep, isMobile]);

  const renderStep = () => {
    if (isCompletePage || isBookingComplete) {
      console.log("Rendering BookingComplete component");
      return <BookingComplete />;
    }

    switch (currentStep) {
      case 1:
        return <ProviderStep />;
      case 2:
        return <ServiceStep />;
      case 3:
        return <ConfirmStep />;
      default:
        return <ProviderStep />;
    }
  };

  const getStepTitle = () => {
    if (isCompletePage || isBookingComplete) {
      return "Buchung abgeschlossen";
    }

    switch (currentStep) {
      case 1:
        return isMobile ? "Anbieter wählen" : "1. Wähle einen Anbieter";
      case 2:
        return isMobile ? "Service & Termin wählen" : "2. Wähle Service, Personal & Termin";
      case 3:
        return isMobile ? "Buchung bestätigen" : "3. Bestätige deine Buchung";
      default:
        return isMobile ? "Anbieter wählen" : "1. Wähle einen Anbieter";
    }
  };

  const getMaxSteps = () => {
    return 3; // Fixed number of steps - explicitly setting to 3
  };

  // Extract studio name and service name for SEO
  const [studioMeta, setStudioMeta] = useState<{
    name?: string;
    location?: string;
    service?: string;
  }>({});
  
  // Add effect to get studio metadata for SEO
  useEffect(() => {
    if (selectedProvider) {
      setStudioMeta({
        name: selectedProvider.name,
        location: selectedProvider.adresse?.split(',')[0],
        service: selectedService?.name
      });
    }
  }, [selectedProvider, selectedService]);

  return (
    <>
      {/* Add BookingSEO component */}
      <BookingSEO 
        studioName={studioMeta.name}
        studioLocation={studioMeta.location}
        serviceName={studioMeta.service}
      />
      
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        transition={{ duration: 0.5 }} 
        className="min-h-screen bg-chrona-background py-4 md:py-8"
      >
        <div className="container mx-auto px-4">
          {/* Progress Indicator - only shown when not on complete screen */}
          {!isBookingComplete && !isCompletePage && (
            <div className="mb-6 md:mb-12 py-0">
              <motion.h1 
                initial={{ opacity: 0, y: -10 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.5, delay: 0.1 }} 
                className="text-2xl md:text-3xl font-bold mb-6 md:mb-8 text-center text-chrona-text py-0 mt-4 md:mt-8 md:my-[72px]"
              >
                {getStepTitle()}
              </motion.h1>
              
              <div className="flex justify-center items-center w-full">
                <motion.div 
                  className="flex items-center justify-center w-full max-w-md mx-auto" 
                  initial={{ opacity: 0, y: 10 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  {Array.from({ length: getMaxSteps() }).map((_, index) => (
                    <div key={`step-${index + 1}`} className="flex items-center justify-center flex-1 relative">
                      <div 
                        className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                          currentStep > index + 1 
                            ? 'bg-chrona-accent text-white shadow-md shadow-chrona-accent/30' 
                            : currentStep === index + 1 
                            ? 'bg-chrona-accent text-white ring-4 ring-chrona-accent/30 shadow-lg shadow-chrona-accent/40' 
                            : 'bg-chrona-gray-700 text-chrona-gray-300'
                        }`}
                      >
                        {currentStep > index + 1 ? <CheckCircle className="w-5 h-5" /> : index + 1}
                      </div>
                      {index < getMaxSteps() - 1 && (
                        <div 
                          className={`absolute top-5 left-[calc(50%+8px)] right-[calc(50%-8px)] h-[3px] z-0 md:top-6 md:left-[calc(50%+12px)] md:right-[calc(50%-12px)] ${
                            currentStep > index + 1 
                              ? 'bg-gradient-to-r from-chrona-accent to-chrona-accent/70' 
                              : 'bg-chrona-gray-600'
                          }`}
                        ></div>
                      )}
                    </div>
                  ))}
                </motion.div>
              </div>
              
              {/* Selection Summary - More mobile friendly */}
              {(!isMobile || currentStep > 1) && (selectedProvider || selectedService || selectedDate) && (
                <motion.div 
                  className="mt-6 md:mt-8 backdrop-blur-xl bg-black/40 border border-white/10 p-4 md:p-5 rounded-xl shadow-xl" 
                  initial={{ opacity: 0, y: 10 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ duration: 0.4, delay: 0.25 }}
                >
                  <h3 className="font-medium mb-2 md:mb-3 text-chrona-text text-sm md:text-base">
                    Deine Auswahl:
                  </h3>
                  <div className="flex flex-wrap gap-1.5 md:gap-2 text-xs md:text-sm">
                    {selectedProvider && (
                      <span className="bg-chrona-accent/20 text-chrona-accent px-2.5 py-1 md:px-3 md:py-1.5 rounded-full border border-chrona-accent/30 inline-block mb-1">
                        {isMobile ? selectedProvider.name : `Anbieter: ${selectedProvider.name}`}
                      </span>
                    )}
                    {selectedService && (
                      <span className="bg-chrona-accent/20 text-chrona-accent px-2.5 py-1 md:px-3 md:py-1.5 rounded-full border border-chrona-accent/30 inline-block mb-1">
                        {isMobile 
                          ? `${selectedService.name} (${selectedService.preis.toFixed(2)} €)` 
                          : `Leistung: ${selectedService.name} (${selectedService.preis.toFixed(2)} €)`
                        }
                      </span>
                    )}
                    {selectedStaff && (
                      <span className="bg-chrona-accent/20 text-chrona-accent px-2.5 py-1 md:px-3 md:py-1.5 rounded-full border border-chrona-accent/30 inline-block mb-1">
                        {isMobile 
                          ? selectedStaff.name 
                          : `Mitarbeiter: ${selectedStaff.name}`
                        }
                      </span>
                    )}
                    {selectedDate && (
                      <span className="bg-chrona-accent/20 text-chrona-accent px-2.5 py-1 md:px-3 md:py-1.5 rounded-full border border-chrona-accent/30 inline-block mb-1">
                        {selectedDate.toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric'})}
                        {selectedTime ? ` um ${selectedTime} Uhr` : ''}
                      </span>
                    )}
                  </div>
                </motion.div>
              )}
            </div>
          )}
          
          {/* Main Content with AnimatePresence for smooth transitions */}
          <div className="max-w-4xl mx-auto">
            <AnimatePresence mode="wait">
              <motion.div 
                key={currentStep + (isCompletePage ? '-complete' : '')} 
                initial={{ opacity: 0, x: 10 }} 
                animate={{ opacity: 1, x: 0 }} 
                exit={{ opacity: 0, x: -10 }} 
                transition={{ duration: 0.25 }}
                className="mb-24 md:mb-16"
              >
                {renderStep()}
              </motion.div>
            </AnimatePresence>
            
            {/* Navigation Buttons - Hidden on mobile as we use the floating button instead */}
            {!isBookingComplete && !isCompletePage && !isMobile && (
              <motion.div 
                className="mt-12 flex justify-between" 
                initial={{ opacity: 0, y: 10 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ delay: 0.3 }}
              >
                <Button 
                  variant="outline" 
                  onClick={goToPreviousStep} 
                  className="border-white/10 hover:bg-white/10 transition-all"
                >
                  {currentStep > 1 ? 'Zurück' : 'Abbrechen'}
                </Button>
                
                {currentStep < getMaxSteps() && (
                  <Button 
                    data-booking-next
                    disabled={!canMoveToNextStep()} 
                    onClick={goToNextStep} 
                    className="bg-chrona-accent hover:bg-chrona-accent-light text-chrona-text shadow-md shadow-chrona-accent/20"
                  >
                    Weiter
                  </Button>
                )}
              </motion.div>
            )}
          </div>
        </div>
        
        {/* Improved mobile floating button with better animation and positioning */}
        {isMobile && !isBookingComplete && !isCompletePage && (
          <motion.div 
            ref={mobileBottomRef}
            className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-t from-[#191919] via-[#1A1A1A]/95 to-[#1A1A1A]/0 pt-8 pb-6 px-4" 
            initial={{ y: 100, opacity: 0 }} 
            animate={{ y: 0, opacity: 1 }} 
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
          >
            <motion.div 
              className="flex gap-3 w-full max-w-md mx-auto" 
              whileHover={{ scale: 1.01 }}
            >
              <Button 
                variant="outline" 
                onClick={goToPreviousStep} 
                className="flex-1 h-14 border-white/10 backdrop-blur-lg bg-black/40 text-white hover:bg-white/10 transition-all rounded-2xl active:scale-95 gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>{currentStep > 1 ? 'Zurück' : 'Abbrechen'}</span>
              </Button>
              
              <Button 
                data-booking-next
                disabled={!canMoveToNextStep()} 
                onClick={goToNextStep} 
                className="flex-[2] h-14 bg-chrona-accent text-white rounded-2xl hover:bg-chrona-accent-light transform transition-all duration-200 active:scale-95 shadow-xl shadow-chrona-accent/30 gap-2"
              >
                <span>{currentStep < getMaxSteps() ? 'Weiter' : 'Buchen'}</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </motion.div>
          </motion.div>
        )}
      </motion.div>
    </>
  );
};

export default BookingFlow;
